// The billing page drivers get. Company uses the BOLT business contract.
// A Romanian individual uses the named address. Any other allowed tax country keeps name, free-text address, and email.
import {
    Alert,
    Box,
    Button,
    TextField,
    Typography,
} from '@mui/material';
import { LocalizationKeys } from 'assets/localizations/localizationKeys';
import { observer } from 'mobx-react';
import React from 'react';
import { useLocation, useParams } from 'react-router-dom';
import {
    PaymentTerminalBillingDetails,
} from 'services/centralServerService';
import { billingApi, BillingSession } from 'services/businessBilling';
import { parseRestoredState, parseSubmissionRecovery } from 'services/businessBilling/restore';
import { useStores } from 'stores/storeInitializer';
import { useBusinessBilling } from './business/useBusinessBilling';
import { getErrorDisplay } from './components/BusinessError/errorDisplay';
import { CompanyCard } from './components/CompanyCard';
import { ErrorActions } from './components/ErrorActions';
import { JourneySwitch } from './components/JourneySwitch';
import { LookupProgress } from './components/LookupProgress';
import { SubmittedSuccess } from './components/SubmittedSuccess';
import { NamedIndividualForm, NamedIndividualState } from './NamedIndividualForm';

type RecipientType = 'BUSINESS' | 'INDIVIDUAL';
type BillingDetailsError = 'submit' | null;

interface BillingDetailsFormState {
    recipientType: RecipientType;
    taxId: string;
    taxCountryCode: string;
}

const emptyNamed: NamedIndividualState = { complete: false, details: null };
const emptyFreeText: NamedIndividualState = { complete: false, details: null };

const FreeTextIndividualForm: React.FC<{
    taxCountryCode: string;
    onChange: (state: NamedIndividualState) => void;
}> = ({ taxCountryCode, onChange }) => {
    const { store } = useStores();
    const [name, setName] = React.useState('');
    const [address, setAddress] = React.useState('');
    const [email, setEmail] = React.useState('');

    React.useEffect(() => {
        const trimmedName = name.trim();
        const trimmedAddress = address.trim();
        const trimmedEmail = email.trim();
        const details = trimmedName || trimmedAddress || trimmedEmail ? {
            schemaVersion: 1 as const,
            ...(trimmedName ? {
                recipient: { type: 'INDIVIDUAL' as const, name: trimmedName, taxCountryCode },
            } : {}),
            ...(trimmedAddress ? { address: { rawAddress: trimmedAddress } } : {}),
            ...(trimmedEmail ? { contact: { email: trimmedEmail } } : {}),
        } : null;
        onChange({ complete: Boolean(details), details });
    }, [name, address, email, taxCountryCode, onChange]);

    return (
        <>
            <TextField
                label={store.L(LocalizationKeys.BillingDetailsName)}
                value={name}
                onChange={(event) => setName(event.target.value)}
                fullWidth
            />
            <TextField
                label={store.L(LocalizationKeys.BillingDetailsAddress)}
                value={address}
                onChange={(event) => setAddress(event.target.value)}
                fullWidth
                multiline
                minRows={3}
            />
            <TextField
                label={store.L(LocalizationKeys.BillingDetailsEmail)}
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                fullWidth
                type="email"
            />
        </>
    );
};

export const B2bBillingDetails: React.FC<{ sessionContext: BillingSession }> = observer(({ sessionContext }) => {
    const { tenantId, token } = useParams<{
        tenantId: string;
        token: string;
    }>();
    const location = useLocation();
    const searchParams = React.useMemo(
        () => new URLSearchParams(location.search),
        [location.search],
    );
    const verificationToken = searchParams.get('verificationToken');
    const { store } = useStores();
    // The selector supplies one saved response. Apply its outcome before the first editable render.
    const restored = React.useMemo(() => parseRestoredState(sessionContext.businessState), [sessionContext.businessState]);
    const pendingSubmission = sessionContext.status === 'SUBMITTED' && !sessionContext.submitted && restored?.kind !== 'submitted';
    const recovery = React.useMemo(() => pendingSubmission
        ? parseSubmissionRecovery(sessionContext.businessSubmissionRecovery) : null,
    [pendingSubmission, sessionContext.businessSubmissionRecovery]);
    const [form, setForm] = React.useState<BillingDetailsFormState>({
        recipientType: 'BUSINESS',
        taxId: recovery?.business.cui ?? (restored?.kind === 'eligible' ? restored.response.company.tax.cui : ''),
        taxCountryCode: '',
    });
    const [named, setNamed] = React.useState<NamedIndividualState>(emptyNamed);
    const [freeText, setFreeText] = React.useState<NamedIndividualState>(emptyFreeText);
    const onNamedChange = React.useCallback((state: NamedIndividualState) => {
        setNamed((current) => (
            current.complete === state.complete && current.details === state.details ? current : state
        ));
    }, []);
    const onFreeTextChange = React.useCallback((state: NamedIndividualState) => {
        setFreeText((current) => (
            current.complete === state.complete && current.details === state.details ? current : state
        ));
    }, []);
    // The Personal journey and the skip still use the legacy submit.
    const [legacySubmitting, setLegacySubmitting] = React.useState(false);
    const [legacySubmitted, setLegacySubmitted] = React.useState(false);
    const [error, setError] = React.useState<BillingDetailsError>(null);
    const legacySubmitLock = React.useRef(false);
    const cuiInputRef = React.useRef<HTMLInputElement | null>(null);
    const recipientTypeEdited = React.useRef(restored !== null && restored.kind !== 'submitted');
    const taxCountryEdited = React.useRef(false);

    const policyForm = sessionContext?.billingDetailsCollection?.form;
    const recipientTypes = React.useMemo(
        () => (
            policyForm?.recipientTypes?.length
                ? policyForm.recipientTypes
                : ['BUSINESS', 'INDIVIDUAL'] as RecipientType[]
        ),
        [policyForm],
    );
    const allowedTaxCountryCodes = React.useMemo(
        () => (
            policyForm?.allowedTaxCountryCodes?.length
                ? policyForm.allowedTaxCountryCodes.map((countryCode) => countryCode.toUpperCase())
                : []
        ),
        [policyForm],
    );

    React.useEffect(() => {
        if (!policyForm) {
            return;
        }
        setForm((current) => {
            const configuredRecipientType = policyForm.defaultRecipientType as RecipientType | undefined;
            const defaultRecipientType = configuredRecipientType && recipientTypes.includes(configuredRecipientType)
                ? configuredRecipientType
                : recipientTypes[0] || 'BUSINESS';
            const nextRecipientType = pendingSubmission ? 'BUSINESS' : !recipientTypeEdited.current
                ? defaultRecipientType
                : recipientTypes.includes(current.recipientType)
                    ? current.recipientType
                    : defaultRecipientType;
            const configuredTaxCountry = (policyForm.defaultTaxCountryCode || '').trim().toUpperCase();
            const defaultTaxCountry = configuredTaxCountry && allowedTaxCountryCodes.includes(configuredTaxCountry)
                ? configuredTaxCountry
                : allowedTaxCountryCodes[0] || '';
            const nextTaxCountry = !taxCountryEdited.current
                ? defaultTaxCountry
                : allowedTaxCountryCodes.includes(current.taxCountryCode)
                    ? current.taxCountryCode
                    : defaultTaxCountry;
            return {
                ...current,
                recipientType: nextRecipientType as RecipientType,
                taxCountryCode: nextTaxCountry,
            };
        });
    }, [policyForm, recipientTypes, allowedTaxCountryCodes, pendingSubmission]);

    const isCompany = form.recipientType === 'BUSINESS';
    const personalTaxCountry = allowedTaxCountryCodes.length === 1
        ? allowedTaxCountryCodes[0]
        : form.taxCountryCode || (allowedTaxCountryCodes.includes('RO') || allowedTaxCountryCodes.length === 0 ? 'RO' : allowedTaxCountryCodes[0]);
    const romanianPersonal = !isCompany && personalTaxCountry === 'RO';
    const showPersonalTaxCountry = !isCompany && allowedTaxCountryCodes.length > 1;
    const business = useBusinessBilling({
        tenantId,
        token,
        verificationToken,
        isCompany,
        rawCui: form.taxId,
        restored,
        pendingSubmission,
        recovery,
        expiresAt: sessionContext.expiresAt,
    });

    const updateField =
        (field: keyof BillingDetailsFormState) =>
            (event: React.ChangeEvent<HTMLInputElement>) => {
                if (business.inFlight || business.pendingSubmission) return;
                setForm((current) => ({
                    ...current,
                    [field]: event.target.value,
                }));
            };

    const selectRecipientType = (recipientType: RecipientType) => {
        if (business.inFlight || business.pendingSubmission) return;
        recipientTypeEdited.current = true;
        setNamed(emptyNamed);
        setFreeText(emptyFreeText);
        setForm((current) => ({ ...current, recipientType }));
    };

    const selectTaxCountry = (taxCountryCode: string) => {
        if (business.inFlight || business.pendingSubmission) return;
        taxCountryEdited.current = true;
        setNamed(emptyNamed);
        setFreeText(emptyFreeText);
        setForm((current) => ({ ...current, taxCountryCode }));
    };

    const submitLegacyBillingDetails = async (billingDetails: PaymentTerminalBillingDetails) => {
        // Two clicks in the same tick must send one request, before the disabled state is rendered.
        if (!tenantId || !token || legacySubmitLock.current || business.inFlight || business.pendingSubmission) {
            return;
        }
        legacySubmitLock.current = true;
        setLegacySubmitting(true);
        setError(null);
        try {
            await billingApi.legacySubmit(
                tenantId,
                token,
                verificationToken,
                billingDetails,
            );
            setLegacySubmitted(true);
        } catch {
            setError('submit');
        } finally {
            legacySubmitLock.current = false;
            setLegacySubmitting(false);
        }
    };

    const submit = async (event: React.FormEvent) => {
        event.preventDefault();
        if (isCompany) {
            business.submit.submit();
            return;
        }
        const personalDetails = romanianPersonal ? named.details : freeText.details;
        if (!personalDetails) {
            return;
        }
        await submitLegacyBillingDetails(personalDetails);
    };

    const continueWithoutBillingDetails = async () => {
        await submitLegacyBillingDetails({ schemaVersion: 1 });
    };

    const errorDisplay = business.errorSource ? getErrorDisplay(business.errorSource) : null;
    const inlineError = errorDisplay?.placement === 'field';

    // A session error permits only starting again from the terminal, so the rest of the form is locked.
    const { actions, sessionLocked } = business;
    const locked = legacySubmitting || business.inFlight || business.pendingSubmission;
    // The panel offers the skip itself, so the persistent skip button is hidden then (never two identical buttons).
    const showBottomSkip = !business.pendingSubmission && !sessionLocked && !actions.includes('CONTINUE_WITHOUT_BILLING');

    const editCui = () => {
        cuiInputRef.current?.focus();
        cuiInputRef.current?.select();
    };

    if (legacySubmitted || business.submitted) {
        return (
            <Box p={3} maxWidth={520} mx="auto">
                <SubmittedSuccess company={business.submitted ? business.company ?? undefined : undefined} />
            </Box>
        );
    }

    return (
        <Box
            component="form"
            onSubmit={submit}
            display="flex"
            flexDirection="column"
            gap={2}
            p={3}
            maxWidth={520}
            mx="auto"
        >
            <Typography variant="h5">
                {store.L(LocalizationKeys.BillingDetailsTitle)}
            </Typography>
            <Typography color="text.secondary">
                {store.L(LocalizationKeys.BillingDetailsBody)}
            </Typography>
            <JourneySwitch
                value={form.recipientType}
                onChange={selectRecipientType}
                options={recipientTypes}
                disabled={locked || sessionLocked}
            />
            {isCompany ? (
                <>
                    <TextField
                        label={store.L(LocalizationKeys.BillingDetailsCui)}
                        value={form.taxId}
                        onChange={updateField('taxId')}
                        fullWidth
                        inputRef={cuiInputRef}
                        disabled={sessionLocked || locked}
                        error={inlineError}
                        helperText={inlineError && errorDisplay
                            ? store.L(errorDisplay.messageKey)
                            : undefined}
                    />
                    {business.lookup.loading ? <LookupProgress /> : null}
                    {business.company ? <CompanyCard data={business.company} /> : null}
                    {errorDisplay?.placement === 'form' ? (
                        <Alert severity="error">{store.L(errorDisplay.messageKey)}</Alert>
                    ) : null}
                    {actions.length > 0 ? (
                        <ErrorActions
                            actions={actions}
                            retryAvailableAt={business.retryAvailableAt}
                            retriesLeft={business.retriesLeft}
                            disabled={locked}
                            onEditCui={editCui}
                            onRetry={business.retry}
                            onSwitchToIndividual={() => selectRecipientType('INDIVIDUAL')}
                            onContinueWithout={continueWithoutBillingDetails}
                        />
                    ) : null}
                </>
            ) : (
                <>
                    {showPersonalTaxCountry ? (
                        <TextField
                            select
                            SelectProps={{ native: true }}
                            label={store.L(LocalizationKeys.BillingDetailsTaxCountry)}
                            value={personalTaxCountry}
                            onChange={(event) => selectTaxCountry(event.target.value)}
                            fullWidth
                            disabled={sessionLocked}
                        >
                            {allowedTaxCountryCodes.map((countryCode) => (
                                <option key={countryCode} value={countryCode}>{countryCode}</option>
                            ))}
                        </TextField>
                    ) : null}
                    {romanianPersonal ? (
                        <NamedIndividualForm key={personalTaxCountry} onChange={onNamedChange} />
                    ) : (
                        <FreeTextIndividualForm
                            key={personalTaxCountry}
                            taxCountryCode={personalTaxCountry}
                            onChange={onFreeTextChange}
                        />
                    )}
                </>
            )}
            <Button
                type="submit"
                variant="contained"
                disabled={legacySubmitting || (isCompany
                    ? !business.submit.allowed
                    : !(romanianPersonal ? named.complete : freeText.complete))}
            >
                {store.L(LocalizationKeys.BillingDetailsSubmit)}
            </Button>
            {showBottomSkip ? (
                <Button
                    type="button"
                    variant="outlined"
                    disabled={locked}
                    onClick={continueWithoutBillingDetails}
                >
                    {store.L(LocalizationKeys.BillingDetailsContinueWithout)}
                </Button>
            ) : null}
            {error === 'submit' ? (
                <Alert severity="error">
                    {store.L(LocalizationKeys.BillingDetailsError)}
                </Alert>
            ) : null}
        </Box>
    );
});

export default B2bBillingDetails;
